import React, { FC, PropsWithChildren, ReactNode } from 'react';

import './ItvRowCollection.scss';
import { layoutRegistry } from './layouts/layoutRegistry';
import { LayoutRenderer, RowCollectionLayout } from './layouts/types';
import { ItvCollectionType } from '../types';
import { getImageUrl } from '../../../utils/navigation';
import { MediaBreakpoint, useBreakpointInfo } from '../../../hooks/useDeviceInfo';
import { getPlaceholder } from '../ItvShared/collections';

type ItvRowCollectionProps = PropsWithChildren<{
  type: ItvCollectionType;
  layout_type: RowCollectionLayout;
  title?: string;
  className?: string;
  id?: string;
  fluidHeight?: boolean;
  showViewAll?: boolean;
  onViewAll?: () => void;
  viewAllLabel?: string;
  headerAction?: ReactNode;
  backgroundEnabled?: boolean;
  backgroundImage?: string | null;
  backgroundImageMob?: string | null;
}>;

const LayoutFallback: LayoutRenderer = ({ layoutType, placeholder }) => (
  <div className="itv-row-collection__fallback">
    <p className="itv-row-collection__fallback-title">Layout "{layoutType}" is not available yet.</p>
    {placeholder}
  </div>
);

export const ItvRowCollection: FC<ItvRowCollectionProps> = ({
  type,
  layout_type,
  title,
  className,
  id,
  children,
  fluidHeight = false,
  showViewAll = true,
  onViewAll,
  viewAllLabel = 'View all',
  headerAction,
  backgroundEnabled,
  backgroundImage,
  backgroundImageMob,
}) => {
  const isMobile = useBreakpointInfo([MediaBreakpoint.BELOW_TABLET]);
  const LayoutComponent = layoutRegistry[layout_type] ?? LayoutFallback;
  const placeholder = getPlaceholder(type, layout_type);
  const bgSrc = isMobile && backgroundImageMob ? backgroundImageMob : backgroundImage;
  const hasBackground = backgroundEnabled && !!bgSrc;
  const sectionClassName = [
    'itv-row-collection',
    `itv-row-collection--${layout_type}`,
    `itv-row-collection--content-${type}`,
    fluidHeight && 'itv-row-collection--fluid-height',
    hasBackground && 'itv-row-collection--has-background',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const sectionStyle = hasBackground ? { backgroundImage: `url(${getImageUrl(bgSrc)})` } : undefined;

  return (
    <section className={sectionClassName} id={id} data-layout={layout_type} style={sectionStyle}>
      <header className="itv-row-collection__header">
        <div className="itv-row-collection__header-text">
          {title && <h2 className="itv-row-collection__title">{title}</h2>}
        </div>
        {headerAction && <div className="itv-row-collection__header-action">{headerAction}</div>}
      </header>

      <LayoutComponent layoutType={layout_type} placeholder={placeholder} contentType={type}>
        {children}
      </LayoutComponent>
      {showViewAll && (
        <div className="itv-row-collection__view-all-wrapper">
          <button
            type="button"
            className="itv-row-collection__view-all"
            onClick={onViewAll}
            aria-label={`${viewAllLabel} ${title}`}
          >
            {viewAllLabel}
          </button>
        </div>
      )}
    </section>
  );
};
